import { createContext, useContext } from "react";
import type { CurrentUser } from "./use-current-user";

/**
 * Who was signed in here last, kept so the application can open with no network (US-33.4, EP-33
 * decision 4; ADR-018).
 *
 * <p>Read the second sentence of that decision before changing this file: <strong>no token is
 * stored</strong>. ADR-004 keeps tokens in memory, and this does not soften it — what is kept is the
 * answer to `GET /api/v1/me`, which is a name, a roastery, a set of roles and a set of permissions.
 * That is enough to draw the screens the person was looking at and to hide the buttons they never
 * had; it is not enough to reach the server, and nothing here tries to be.
 *
 * <p>So the permissions in this file are <em>ergonomics</em>, not security. Every write made in this
 * state goes to the queue and is sent later with a real token, against a server that checks the same
 * matrix again. A tampered copy of this record buys somebody a button that answers 403.
 *
 * <p>`localStorage`, not IndexedDB: it must be readable synchronously, before the first render, by a
 * guard deciding whether to send the browser to an identity provider that is not answering.
 */

const KEY = "roastery.offline.user";

export interface RememberedIdentity {
  user: CurrentUser;
  /** When this record was written — which is also "as of" for everything else on the device. */
  savedAt: number;
}

export function rememberIdentity(user: CurrentUser): void {
  if (!user?.id) {
    return;
  }
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ user, savedAt: Date.now() }));
  } catch {
    // Private mode, or site data switched off. Offline mode simply will not be offered.
  }
}

export function rememberedIdentity(): RememberedIdentity | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as RememberedIdentity;
    return parsed?.user?.id ? parsed : null;
  } catch {
    return null;
  }
}

/** Signing out takes the name off the device; what it leaves behind is cleared by the caller. */
export function forgetIdentity(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Nothing to do: a record that cannot be removed was never written.
  }
}

/**
 * That the application is running on this record rather than on a session.
 *
 * <p>Set by {@link RequireAuth} and by nothing else: the guard is the one place that knows the
 * identity provider could not be reached, and everything below has to agree with its decision.
 * Without a single answer, one screen would draw the remembered user while another decided there was
 * nobody signed in — which is how a platform-operator screen ends up in front of a roaster.
 */
export const OfflineIdentityContext = createContext<RememberedIdentity | null>(null);

export function useOfflineIdentity(): RememberedIdentity | null {
  return useContext(OfflineIdentityContext);
}
